import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useEffect,
  useContext,
  useCallback
} from 'react';
import classNames from 'classnames';
import { Document } from 'react-pdf';
import VirtualList, { ListRef } from 'rc-virtual-list';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import PanelAction from '@pages/MediaViewer/AssetViewer/PanelAction';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { DocumentMetadataDto } from '@api/Api';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import PageThumbnail from '@pages/MediaViewer/AssetViewer/PdfCard/components/PageThumbnail';
import PdfPage from '@pages/MediaViewer/AssetViewer/PdfCard/components/PdfPage';
import 'react-pdf/dist/Page/TextLayer.css';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import {
  getInitialPdfPageScale,
  handleSelectionChange
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import type { PdfCardProps } from '@pages/MediaViewer/AssetViewer/PdfCard/PdfCard';
import { usePdfFabricCanvas } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfFabricCanvas';
import { usePdfScroll } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfScroll';
import { usePdfZoomScrollRestore } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfZoomScrollRestore';
import {
  ArrowLeftWithoutLineIcon,
  ArrowRightWithoutLineIcon,
  MediaViewerPagesIcon,
  SlideTextIcon
} from '@icons';
import {
  isAnnotationDrawTool,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';

const maxCanvasSize = 2048;
const minZoom = 0.1;
const maxZoom = 3;
const zoomStep = 0.1;
const pageVerticalPadding = 16;

function clampZoom(value: number) {
  return Math.min(maxZoom, Math.max(minZoom, value));
}

function extractTextFromNode(node: Node): string {
  if (node.nodeName === 'BR') return '\n';
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType === Node.ELEMENT_NODE)
    return Array.from(node.childNodes).map(extractTextFromNode).join('');
  return '';
}

export default memo(function PdfCardDesktop({
  isExpand,
  setIsExpand,
  onReady,
  asset,
  isAssetReady,
  mobileActionVisibility
}: PdfCardProps) {
  const fabricContext = useContext(FabricContext);
  const {
    selectedCommentId,
    shapesDrawingTools,
    canvasMode,
    activePageNumber,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    documentHighlight,
    selectComment,
    setActivePageNumber,
    setDocumentHighlight,
    setTurnoverDegree,
    setZoomFactor,
    setZoomOption,
    setCanvasMode,
    setActivePageHasText
  } = useAssetViewerState();
  const isShapeDrawing = shapesDrawingTools.shape !== CURSOR_DRAW_TOOL;
  const isTextHighlightMode = shapesDrawingTools.shape === 'documentHighlight';
  const isMainCanvasVisible =
    !!selectedCommentId ||
    (isAnnotationDrawTool(shapesDrawingTools.shape) && canvasMode === 'edit');
  const documentHighlightRef = useRef(documentHighlight);

  const listRef = useRef<ListRef>(null);
  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const buttonSelectedTextRef = useRef<HTMLButtonElement>(null);
  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const pageRefsMap = useRef(new Map<number, HTMLDivElement>());
  const skipActivePageScrollSyncRef = useRef(false);
  const isLoadPdf = useRef<boolean>(false);
  const pagesReadyRef = useRef(new Set<number>());
  const pageHasTextRef = useRef(new Map<number, boolean>());

  const textLayersMapRef = useRef<Map<HTMLDivElement, HTMLDivElement>>(
    new Map()
  );

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as DocumentMetadataDto;
    return proxy.url || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  useEffect(() => {
    isLoadPdf.current = false;
    pagesReadyRef.current.clear();
    pageHasTextRef.current.clear();
    setActivePageHasText(true);
    setIsPageSizeReady(false);
  }, [asset.id, setActivePageHasText]);

  const [pagesCount, setPagesCount] = useState(1);
  const [pageRefsVersion, setPageRefsVersion] = useState(0);
  const [pagesReadyVersion, setPagesReadyVersion] = useState(0);
  const [isShowPages, setIsShowPages] = useState<boolean>(true);
  const [isPageSizeReady, setIsPageSizeReady] = useState(false);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 1605,
    height: 2272
  });

  const _pageSize = useMemo(() => {
    return {
      width: pageSize.width * zoomFactor,
      height: pageSize.height * zoomFactor
    };
  }, [pageSize, zoomFactor]);

  const pagesArray = useMemo(
    () =>
      new Array(pagesCount).fill('').map((_, index) => ({ page: index + 1 })),
    [pagesCount]
  );

  const rowPitch = useMemo(() => {
    const height = pageSize.height * zoomFactor;
    return height > 0 ? height + 8 : 0;
  }, [pageSize.height, zoomFactor]);

  const onRegisterPageRoot = useCallback(
    (page: number, element: HTMLDivElement | null) => {
      const hadElement = pageRefsMap.current.has(page);
      if (element) {
        pageRefsMap.current.set(page, element);
        if (!hadElement) setPageRefsVersion((value) => value + 1);
        return;
      }
      pageRefsMap.current.delete(page);
      if (hadElement) setPageRefsVersion((value) => value + 1);
    },
    []
  );

  const onPageLoadSuccess = useCallback((page: number) => {
    if (pagesReadyRef.current.has(page)) return;
    pagesReadyRef.current.add(page);
    setPagesReadyVersion((value) => value + 1);
  }, []);

  const onPageTextContent = useCallback(
    (page: number, hasText: boolean) => {
      pageHasTextRef.current.set(page, hasText);
      if (page === activePageNumber) setActivePageHasText(hasText);
    },
    [activePageNumber, setActivePageHasText]
  );

  useEffect(() => {
    setActivePageHasText(
      pageHasTextRef.current.get(activePageNumber) !== false
    );
  }, [activePageNumber, setActivePageHasText]);

  const onSelectText = (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    setDocumentHighlight(documentHighlightRef.current);
  };

  const { runCanvasLayout, onClearHighlightObjects, renderTextHighlights } =
    usePdfFabricCanvas({
      fabricContext,
      wrapperRef,
      contentRef,
      canvasContainerRef,
      pageRefsMap,
      activePageNumber,
      zoomFactor,
      isPageSizeReady,
      selectedCommentId,
      pageRefsVersion,
      pagesReadyVersion,
      isMainCanvasVisible,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    });

  const { renderRange, visiblePageNumber, updateActivePageFromScroll } =
    usePdfScroll({
      wrapperRef,
      pageRefsMap,
      skipActivePageScrollSyncRef,
      pagesArray,
      pagesCount,
      rowPitch,
      isPageSizeReady,
      pageRefsVersion,
      activePageNumber,
      selectedCommentId,
      shapesDrawingTools,
      runCanvasLayout,
      setActivePageNumber,
      selectComment,
      setDocumentHighlight,
      setCanvasMode
    });

  const { captureZoomScrollAnchor } = usePdfZoomScrollRestore({
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef,
    activePageNumber,
    zoomFactor,
    pageRefsVersion,
    runCanvasLayout,
    updateActivePageFromScroll
  });

  const prevHighlightRectsLengthRef = useRef(
    documentHighlight.rects?.length ?? 0
  );

  useEffect(() => {
    const prevLength = prevHighlightRectsLengthRef.current;
    const currentLength = documentHighlight.rects?.length ?? 0;
    prevHighlightRectsLengthRef.current = currentLength;

    if (currentLength > 0) return;

    documentHighlightRef.current = { text: '', rects: [] };
    onClearHighlightObjects();

    if (prevLength > 0) {
      document.getSelection()?.removeAllRanges();
    }
  }, [documentHighlight.rects?.length, onClearHighlightObjects]);

  useEffect(() => {
    if (!isShapeDrawing) return;
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    const buttonSelectedText = buttonSelectedTextRef.current;
    const pageCanvas = pageCanvasRef.current;
    let prevRange: Range | null = null;

    const onSelectionchange = () => {
      textLayersMapRef.current.clear();
      pageRefsMap.current.forEach((pageRoot) => {
        const textLayerDiv = pageRoot?.querySelector('.textLayer');
        const endDiv = pageRoot?.querySelector('.endOfContent');
        if (!textLayerDiv || !endDiv) return;
        textLayersMapRef.current.set(
          textLayerDiv as HTMLDivElement,
          endDiv as HTMLDivElement
        );
      });

      const range = handleSelectionChange(textLayersMapRef.current, prevRange);
      prevRange = range;

      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'none';
        buttonSelectedText.style.pointerEvents = 'none';
      }

      if (!range) {
        onClearHighlightObjects();
        return;
      }

      const pageRoot = pageRefsMap.current.get(activePageNumber);
      const pageCoordsElement =
        pageRoot?.querySelector('.media_viewer_pdf-inner') ?? pageCanvas;

      if (!pageCoordsElement) {
        onClearHighlightObjects();
        return;
      }

      const rects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0
      );
      if (!rects.length) {
        onClearHighlightObjects();
        if (buttonSelectedText) {
          buttonSelectedText.style.display = 'none';
        }
        return;
      }
      const canvasRect = pageCoordsElement.getBoundingClientRect();
      const selectedCoords = rects
        .map((rect) => ({
          left: ((rect.left - canvasRect.left) / canvasRect.width) * 100,
          top: ((rect.top - canvasRect.top) / canvasRect.height) * 100,
          width: (rect.width / canvasRect.width) * 100,
          height: (rect.height / canvasRect.height) * 100
        }))
        .filter((r) => r.width > 0 && r.height > 0);

      if (!selectedCoords.length) {
        onClearHighlightObjects();
        return;
      }

      const fragment = range.cloneContents();
      documentHighlightRef.current = {
        text: Array.from(fragment.childNodes)
          .map(extractTextFromNode)
          .join('')
          .trim(),
        rects: selectedCoords
      };
      renderTextHighlights(selectedCoords);

      const textLayerDiv = pageRoot?.querySelector('.textLayer');
      const contentEl = contentRef.current;
      if (!textLayerDiv || !contentEl) return;
      const contentRect = contentEl.getBoundingClientRect();
      const lastRect = rects[rects.length - 1];
      const buttonWidth = buttonSelectedText?.offsetWidth || 0;
      const buttonHeight = buttonSelectedText?.offsetHeight || 0;
      const left = Math.min(
        Math.max(lastRect.right - contentRect.left + 8, 0),
        contentRect.width - buttonWidth
      );
      const top = Math.min(
        Math.max(lastRect.bottom - contentRect.top + 8, 0),
        contentRect.height - buttonHeight
      );
      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'block';
        buttonSelectedText.style.left = `${left}px`;
        buttonSelectedText.style.top = `${top}px`;
      }
    };

    const onMouseUp = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    const onTouchEnd = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    document.addEventListener('selectionchange', onSelectionchange);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      prevRange = null;
      textLayersMapRef.current.clear();
      document.removeEventListener('selectionchange', onSelectionchange);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    selectedCommentId,
    shapesDrawingTools,
    isShapeDrawing,
    activePageNumber,
    setDocumentHighlight,
    onClearHighlightObjects,
    renderTextHighlights
  ]);

  useEffect(() => {
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    if (!documentHighlightRef.current?.rects?.length) return;
    renderTextHighlights(documentHighlightRef.current.rects);
  }, [zoomFactor, shapesDrawingTools.shape, renderTextHighlights]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      index: activePageNumber - 1
    });
  }, [isShowPages, activePageNumber]);

  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return clampZoom(1);
      const viewport =
        wrapperRef.current?.osInstance()?.elements()?.viewport ??
        wrapperRef.current?.getElement();
      if (!viewport || !isPageSizeReady) return 1;
      const width =
        Math.abs(pageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(pageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const containerWidth = viewport.clientWidth;
      const containerHeight = Math.max(
        0,
        viewport.clientHeight - pageVerticalPadding
      );
      const scaleWidth = Math.floor(containerWidth / (width / 100)) / 100;
      const scaleHeight = Math.floor(containerHeight / (height / 100)) / 100;
      if (option === 'auto' || option === 'fit-width') {
        return clampZoom(scaleWidth);
      }
      if (option === 'fit-height') {
        return clampZoom(scaleHeight);
      }
      if (option === 'fit-page') {
        return clampZoom(Math.min(scaleWidth, scaleHeight));
      }
      return 1;
    },
    [pageSize, turnoverDegree, isPageSizeReady]
  );

  const setZoom = useCallback(
    (v: number | NonNullable<AssetViewerZoomOption>) => {
      if (typeof v === 'number') {
        const newZoom = clampZoom(v);
        captureZoomScrollAnchor(newZoom);
        setZoomFactor(newZoom);
      } else {
        const newZoomFactor = getZoomFactorFromOption(v);
        captureZoomScrollAnchor(newZoomFactor);
        setZoomFactor(newZoomFactor, v);
      }
    },
    [getZoomFactorFromOption, setZoomFactor, captureZoomScrollAnchor]
  );

  useEffect(() => {
    setZoomOption('fit-page');
  }, [asset.id, setZoomOption]);

  useEffect(() => {
    if (!zoomOption || !isPageSizeReady) return undefined;

    const container =
      wrapperRef.current?.osInstance()?.elements()?.viewport ??
      wrapperRef.current?.getElement();
    if (!container) return undefined;

    const applyZoom = () => {
      const newZoomFactor = getZoomFactorFromOption(zoomOption);
      setZoomFactor(newZoomFactor, zoomOption);
    };

    applyZoom();
    const observer = new ResizeObserver(applyZoom);
    observer.observe(container);
    return () => observer.disconnect();
  }, [zoomOption, getZoomFactorFromOption, setZoomFactor, isPageSizeReady]);

  const onChangePage = useCallback(
    (page: number) => {
      setActivePageNumber(page);
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    },
    [setActivePageNumber, selectComment, setDocumentHighlight, setCanvasMode]
  );

  const onLoadSuccess = useCallback(
    (pdfPage: { originalWidth: number; originalHeight: number }) => {
      if (isLoadPdf.current) return;
      isLoadPdf.current = true;
      const { originalWidth, originalHeight } = pdfPage;
      setPageSize(
        getInitialPdfPageScale(
          originalWidth,
          originalHeight,
          maxCanvasSize,
          maxZoom,
          false
        )
      );
      setIsPageSizeReady(true);
    },
    []
  );

  const showPanelAction =
    !mobileActionVisibility || mobileActionVisibility.showPanelAction;

  const panelActionConfig = useMemo(
    () => ({
      zoom: zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawingToolsOpen: isShapeDrawing,
      isExpand,
      setIsExpand: (v: boolean) => {
        setIsExpand(v);
      },
      setRotate: (v: number) => {
        setTurnoverDegree(turnoverDegree + v === 360 ? 0 : turnoverDegree + v);
      }
    }),
    [
      zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawing,
      isExpand,
      setIsExpand,
      turnoverDegree,
      setTurnoverDegree
    ]
  );

  return (
    <Document
      className={classNames('media_viewer_pdf', {
        'b-show-pages': isShowPages,
        'b-hide-shapes-drawing':
          shapesDrawingTools?.shape === 'documentHighlight' && isShapeDrawing,
        'b-shapes-drawing': isShapeDrawing
      })}
      file={fileUrl}
      onLoadSuccess={(pdf) => {
        setPagesCount(pdf.numPages);
        onReady();
      }}
      loading={<PlaybackPreparingLoader />}
    >
      {isShowPages && (
        <div className="media_viewer_pdf-sidebar">
          <VirtualList
            className="media_viewer_pdf-list"
            data={pagesArray}
            height={wrapperRef?.current?.getElement()?.offsetHeight || 500}
            fullHeight={true}
            itemKey="page"
            ref={listRef}
          >
            {(item) => {
              return (
                <PageThumbnail
                  page={item.page}
                  active={item.page === activePageNumber}
                  maxWidth={102}
                  maxHeight={147}
                  onClick={onChangePage}
                />
              );
            }}
          </VirtualList>
        </div>
      )}
      <div className="media_viewer_pdf-actions">
        <div className="media_viewer_pdf-actions-indicator">
          <button
            className="media_viewer_pdf-btn"
            type="button"
            disabled={activePageNumber === 1}
            onClick={() => onChangePage(activePageNumber - 1)}
          >
            <ArrowLeftWithoutLineIcon />
          </button>
          {visiblePageNumber} of {pagesCount}
          <button
            disabled={activePageNumber === pagesCount}
            className="media_viewer_pdf-btn"
            type="button"
            onClick={() => onChangePage(activePageNumber + 1)}
          >
            <ArrowRightWithoutLineIcon />
          </button>
        </div>
        <button
          className="media_viewer_pdf-btn media_viewer_pdf-action-btn tour-text-highlight-pdf-action"
          type="button"
          onClick={() => setIsShowPages(!isShowPages)}
        >
          <MediaViewerPagesIcon />
        </button>
      </div>
      {showPanelAction && (
        <PanelAction
          {...panelActionConfig}
          minZoom={minZoom}
          zoomStep={zoomStep}
          showFitPage
        />
      )}
      <OverlayScrollbarsComponent
        className="media_viewer_pdf-wrapper"
        ref={wrapperRef}
      >
        <div
          className={classNames(
            'media_viewer_pdf-content',
            'media_viewer_pdf-content-mobile'
          )}
          ref={contentRef}
        >
          <div
            className="media_viewer_pdf-mobile-pages media_viewer_pdf-mobile-pages-desktop"
            onContextMenu={
              isTextHighlightMode ? (e) => e.preventDefault() : undefined
            }
          >
            {pagesArray.map(({ page }) => (
              <PdfPage
                key={page}
                pageNumber={page}
                pageWidth={_pageSize.width}
                pageHeight={_pageSize.height}
                zoomFactor={zoomFactor}
                turnoverDegree={turnoverDegree}
                isActive={page === activePageNumber}
                isMainCanvasVisible={isMainCanvasVisible}
                isFirstPage={page === 1}
                shouldRender={
                  page >= renderRange.start && page <= renderRange.end
                }
                pageCanvasRef={pageCanvasRef}
                onRegisterPageRoot={onRegisterPageRoot}
                onFirstPageLoadSuccess={onLoadSuccess}
                onPageLoadSuccess={onPageLoadSuccess}
                onPageTextContent={onPageTextContent}
              />
            ))}
          </div>
          <button
            ref={buttonSelectedTextRef}
            className="media_viewer_pdf-selected-btn"
            type="button"
            onClick={onSelectText}
            style={{ display: 'none' }}
          >
            <SlideTextIcon />
          </button>
          <div ref={canvasContainerRef} className="media_viewer_pdf-canvas">
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {(!isAssetReady || !isPageSizeReady) && <PlaybackPreparingLoader />}
    </Document>
  );
});
